import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import { Button, Input, IconButton, Skeleton, Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceWebsiteWhitelist,
  useUpdateServiceWebsiteWhitelist,
  useServicePageDefacementSites,
  type WhitelistRule,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './WebsiteWhitelistForm.module.css';

const emptyDraft: WhitelistRule = { path: '', ruleId: '', remark: '' };

/**
 * Edit one website's Website-Whitelist rules (AI-WAF → Web Access Control). Each
 * rule whitelists a URL path; an empty Rule ID bypasses all rules for that path.
 * Persisted in the site config under `website_whitelist`. Route:
 * aiwaf/web-access-control/:siteId.
 */
export function WebsiteWhitelistForm() {
  const navigate = useNavigate();
  const { serviceId, siteId } = useParams<{ serviceId: string; siteId: string }>();
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;

  const { data: existing, isLoading: loadingExisting } = useServiceWebsiteWhitelist(serviceId, siteId);
  const { data: sites = [] } = useServicePageDefacementSites(serviceId);
  const domain = useMemo(
    () => sites.find((s) => String(s.site_id) === siteId)?.domain ?? '',
    [sites, siteId],
  );

  const [rules, setRules] = useState<WhitelistRule[]>([]);
  useEffect(() => {
    if (existing) setRules(existing);
  }, [existing]);

  const [draft, setDraft] = useState<WhitelistRule>(emptyDraft);
  const addRule = () => {
    if (!draft.path.trim()) return;
    setRules((r) => [...r, { ...draft, path: draft.path.trim() }]);
    setDraft(emptyDraft);
  };
  const removeRule = (i: number) => setRules((r) => r.filter((_, idx) => idx !== i));
  const editRule = (i: number) => {
    setDraft({ ...rules[i] });
    removeRule(i);
  };

  const update = useUpdateServiceWebsiteWhitelist(serviceId ?? '', siteId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'website_whitelist', 'edit');
      show({ tone: 'success', title: 'Website whitelist saved' });
      navigate(`${base}/aiwaf/web-access-control`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  if (loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 320 }} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Web Access Control', slug: 'aiwaf/web-access-control' },
              { label: 'Website Whitelist' },
            ]}
          />
        }
        title={domain || 'Website Whitelist'}
        description="When the Rule ID is empty, requests pointing to the specified URLs bypass all rules."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        {/* Website Whitelist Rules: contains a table — kept as Card wrapping the full section */}
        <Card flat>
          <CardHeader>
            <CardTitle>Website Whitelist Rules</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.condRow}>
              <Field label="Path" required>
                {(props) => (
                  <Input
                    {...props}
                    value={draft.path}
                    placeholder="/api/public"
                    className={styles.pathInput}
                    onChange={(e) => setDraft((d) => ({ ...d, path: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addRule();
                      }
                    }}
                  />
                )}
              </Field>
              <Field label="Rule ID">
                {(props) => (
                  <Input
                    {...props}
                    value={draft.ruleId}
                    placeholder="Multiple rule IDs separated by commas"
                    className={styles.ruleIdInput}
                    onChange={(e) => setDraft((d) => ({ ...d, ruleId: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Description">
                {(props) => (
                  <Input
                    {...props}
                    value={draft.remark}
                    placeholder="Description"
                    className={styles.remarkInput}
                    onChange={(e) => setDraft((d) => ({ ...d, remark: e.target.value }))}
                  />
                )}
              </Field>
              <Button variant="secondary" startIcon={<Plus size={14} />} onClick={addRule}>
                Add
              </Button>
            </div>

            <WhitelistTable rows={rules} onEdit={editRule} onRemove={removeRule} />
          </CardBody>
        </Card>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/web-access-control`)}>
            Cancel
          </Button>
          <Button disabled={update.isPending} onClick={() => update.mutate(rules)}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Container>
  );
}

/** Whitelist rules table — Path / Rule ID / Description + Edit/Delete. */
function WhitelistTable({
  rows,
  onEdit,
  onRemove,
}: {
  rows: WhitelistRule[];
  onEdit: (i: number) => void;
  onRemove: (i: number) => void;
}) {
  const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;
  return (
    <div className={styles.table}>
      <div className={styles.tableHead}>
        <div className={styles.cell}>Path</div>
        <div className={styles.cell}>Rule ID</div>
        <div className={styles.cell}>Description</div>
        <div className={styles.cell}>Operation</div>
      </div>
      {rows.length === 0 ? (
        <div className={`${styles.cell} ${styles.cellEmpty}`}>
          No data
        </div>
      ) : (
        rows.map((r, i) => (
          <div key={i} className={styles.tableRow}>
            <div className={styles.cell}>{r.path}</div>
            <div className={styles.cell}>{r.ruleId || muted('All rules')}</div>
            <div className={styles.cell}>{r.remark || muted('—')}</div>
            <div className={`${styles.cell} ${styles.cellActions}`}>
              <IconButton aria-label="Edit" variant="ghost" onClick={() => onEdit(i)}>
                <Pencil size={16} />
              </IconButton>
              <IconButton aria-label="Remove" variant="ghost" onClick={() => onRemove(i)}>
                <Trash2 size={16} />
              </IconButton>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
